// Columna izquierda del explorer (`.md-filters` del mock), en su orden: Búsqueda · Nivel SK ·
// Tendencia · Compras contigo · Última compra contigo. Solo el nombre viaja al servidor.
import type { ReactNode } from 'react';
import { Info, X } from 'lucide-react';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { PortalCustomerStatus } from '@/api/services/retail-wizard/contract';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { cn } from '@/lib/cn';
import { STATUS_LABEL } from '../lib/portalCustomerFormat';
import {
    countActiveFilters,
    EMPTY_FILTERS,
    toggleInList,
    type LevelFilter,
    type PortalCustomersFilters,
} from '../lib/portalCustomersFilters';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'w-full min-w-0 rounded border border-border bg-background text-[11px] tabular-nums text-foreground outline-none focus:border-ring';
const CHIP =
    'cursor-pointer whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold transition-colors';

/** Contorno del color del metal; prendido, su fondo suave. */
const LEVEL_CHIPS: { value: LevelFilter; label: string; off: string; on: string }[] = [
    {
        value: ClientLevelEnum.GOLD,
        label: 'Oro',
        off: 'border-tier-gold bg-card text-tier-gold hover:bg-accent',
        on: 'border-tier-gold bg-tier-gold-soft text-tier-gold',
    },
    {
        value: ClientLevelEnum.SILVER,
        label: 'Plata',
        off: 'border-tier-silver bg-card text-tier-silver hover:bg-accent',
        on: 'border-tier-silver bg-tier-silver-soft text-tier-silver',
    },
    {
        value: ClientLevelEnum.BRONZE,
        label: 'Bronce',
        off: 'border-tier-bronze bg-card text-tier-bronze hover:bg-accent',
        on: 'border-tier-bronze bg-tier-bronze-soft text-tier-bronze',
    },
    {
        value: 'NONE',
        label: 'Sin nivel',
        off: 'border-border bg-card hover:bg-accent',
        on: 'border-primary bg-primary text-primary-foreground',
    },
];

const TREND_CHIPS: PortalCustomerStatus[] = ['ACTIVE', 'REGULAR', 'DORMANT'];

const TREND_TITLE: Record<PortalCustomerStatus, string> = {
    ACTIVE: 'Te compró hace 30 días o menos',
    REGULAR: 'Te compró hace entre 31 y 90 días',
    DORMANT: 'Lleva más de 90 días sin comprarte',
};

/** Rótulo de los filtros que el servidor no aplica, mientras queden páginas por traer. */
const LOADED_ONLY_HINT = 'sobre lo cargado';

export function CustomersFiltersPanel({
    filters,
    onChange,
    collapsed,
    onToggleCollapsed,
    partialScope,
}: {
    filters: PortalCustomersFilters;
    onChange: (next: PortalCustomersFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
    /** Quedan páginas: los filtros que solo miran lo cargado lo dicen. */
    partialScope: boolean;
}) {
    const patch = (partial: Partial<PortalCustomersFilters>) => {
        onChange({ ...filters, ...partial });
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={() => {
                onChange(EMPTY_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <input
                        type="search"
                        value={filters.query}
                        onChange={(e) => {
                            patch({ query: e.target.value });
                        }}
                        placeholder="Nombre o teléfono"
                        aria-label="Buscar cliente por nombre o teléfono"
                        className={cn(
                            FIELD,
                            'py-1 pl-2 pr-[26px] text-[11.5px] [&::-webkit-search-cancel-button]:hidden',
                        )}
                    />
                    {filters.query === '' ? null : (
                        <button
                            type="button"
                            aria-label="Limpiar búsqueda"
                            onClick={() => {
                                patch({ query: '' });
                            }}
                            className="absolute right-1.5 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                            <X className="size-2.5" aria-hidden />
                        </button>
                    )}
                </div>
                <p className="mt-1 text-[9.5px] leading-[1.35] text-muted-foreground">
                    El nombre busca desde el inicio en toda tu cartera; el teléfono, en los clientes
                    cargados.
                </p>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>
                    Nivel SK
                    <span title="Nivel asignado por SureKeep." className="ml-1 cursor-help">
                        <Info
                            className="inline size-2.5 align-[-1px]"
                            aria-label="Nivel asignado por SureKeep."
                        />
                    </span>
                </FilterLabel>
                <div className="flex flex-wrap gap-[3px]">
                    {LEVEL_CHIPS.map((chip) => {
                        const on = filters.levels.includes(chip.value);
                        return (
                            <button
                                key={chip.value}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    patch({ levels: toggleInList(filters.levels, chip.value) });
                                }}
                                className={cn(CHIP, on ? chip.on : chip.off)}
                            >
                                {chip.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>Tendencia</FilterLabel>
                <div className="flex flex-wrap gap-[3px]">
                    {TREND_CHIPS.map((status) => {
                        const on = filters.trends.includes(status);
                        return (
                            <button
                                key={status}
                                type="button"
                                aria-pressed={on}
                                title={TREND_TITLE[status]}
                                onClick={() => {
                                    patch({ trends: toggleInList(filters.trends, status) });
                                }}
                                className={cn(
                                    CHIP,
                                    on
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-border bg-card hover:bg-accent',
                                )}
                            >
                                {STATUS_LABEL[status]}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>Compras contigo (#)</FilterLabel>
                <RangeRow>
                    <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        value={filters.minPurchases}
                        onChange={(e) => {
                            patch({ minPurchases: e.target.value });
                        }}
                        placeholder="mín"
                        aria-label="Compras contigo, mínimo"
                        className={cn(FIELD, 'px-1.5 py-1 text-center')}
                    />
                    <input
                        type="number"
                        min={0}
                        step={1}
                        inputMode="numeric"
                        value={filters.maxPurchases}
                        onChange={(e) => {
                            patch({ maxPurchases: e.target.value });
                        }}
                        placeholder="máx"
                        aria-label="Compras contigo, máximo"
                        className={cn(FIELD, 'px-1.5 py-1 text-center')}
                    />
                </RangeRow>
            </div>

            <div className={GROUP}>
                <FilterLabel partialScope={partialScope}>Última compra contigo</FilterLabel>
                <RangeRow>
                    <input
                        type="date"
                        value={filters.lastFrom}
                        onChange={(e) => {
                            patch({ lastFrom: e.target.value });
                        }}
                        aria-label="Última compra contigo, desde"
                        className={cn(FIELD, 'p-1 text-[10.5px]')}
                    />
                    <input
                        type="date"
                        value={filters.lastTo}
                        onChange={(e) => {
                            patch({ lastTo: e.target.value });
                        }}
                        aria-label="Última compra contigo, hasta"
                        className={cn(FIELD, 'p-1 text-[10.5px]')}
                    />
                </RangeRow>
            </div>
        </FiltersColumn>
    );
}

/** `.flt-range-row` del mock: dos campos con el guion al centro. */
function RangeRow({ children }: { children: [ReactNode, ReactNode] }) {
    return (
        <div className="grid grid-cols-[1fr_12px_1fr] items-center gap-1">
            {children[0]}
            <span className="text-center text-[12px] text-muted-foreground" aria-hidden>
                —
            </span>
            {children[1]}
        </div>
    );
}

/** Encabezado de un grupo que solo filtra lo cargado; con páginas pendientes lo rotula. */
function FilterLabel({ children, partialScope }: { children: ReactNode; partialScope: boolean }) {
    return (
        <div className={LABEL}>
            {children}
            {partialScope ? (
                <span className="ml-1 font-semibold normal-case tracking-normal">
                    · {LOADED_ONLY_HINT}
                </span>
            ) : null}
        </div>
    );
}
